import {
    Heading,
    Stack,
    Text,
    ThemeFontSizeVariable,
    ThemeSpaceVariable,
} from "@caspeco/casper-ui-library";
import { useWebBooking } from "api/api-hooks/use-web-booking";
import { BookingSuccess } from "pages/booking-details/booking-status/booking-success";
import { CancelBookingFailed } from "pages/booking-details/cancel-booking/cancel-booking-failed";
import { CancelBookingSuccess } from "pages/booking-details/cancel-booking/cancel-booking-success";
import Greeting from "pages/booking-details/cancel-booking/greeting";
import React from "react";
import { useTranslation } from "react-i18next";
import { hasBookingPassed, hasWaitListOffers, isBookingCanceled, isWaitList } from "utils/booking";
import GreetingWrapper from "./greeting-wrapper";
import WaitListOffer from "./wait-list-offer";
import { getSearchParams } from "search-params";

interface BookingStatusProps {
    isFinal: boolean;
    cancelSuccessful: boolean;
    isCancelError: boolean;
}

const BookingStatus: React.FC<BookingStatusProps> = ({
    isFinal,
    cancelSuccessful,
    isCancelError,
}) => {
    const webBooking = useWebBooking().data;
    const { t } = useTranslation();

    const { inquiryProposal } = getSearchParams();
    if (!webBooking) return null;

    const guests = webBooking.guests;
    const date = webBooking.start.format("dddd D MMMM");
    const startTime = webBooking.start.format("HH:mm");
    const endTime = webBooking.displayEndTime().format("HH:mm");

    const hasCanceledStatus = isBookingCanceled(webBooking);
    const isWaitListBooking = isWaitList(webBooking);
    const hasPassed = hasBookingPassed(webBooking);

    const proposalRespondSuccess = inquiryProposal && webBooking.proposalAcceptedByGuest();
    const proposalRespondRejected = inquiryProposal && webBooking.proposalRejectedByGuest();

    if ((isFinal && !cancelSuccessful) || proposalRespondSuccess) {
        return <BookingSuccess isWaitList={isWaitListBooking} />;
    }

    if (cancelSuccessful) {
        return <CancelBookingSuccess isWaitList={isWaitListBooking} />;
    }

    if (proposalRespondRejected) {
        return (
            <Stack
                alignItems="flex-start"
                justifyContent="space-between"
                flexDirection="column"
                h="100%"
                pt={ThemeSpaceVariable.Large}
                spacing={ThemeSpaceVariable.None}
            >
                <Heading as="h1" fontWeight="700">
                    {t("bookingRequest.respond.rejected.title")}
                </Heading>
                <Text>{t("bookingRequest.respond.rejected.message")}</Text>
            </Stack>
        );
    }

    if (webBooking.proposalTimeout()) {
        return (
            <GreetingWrapper name={webBooking.contact.fname}>
                <Text fontSize={ThemeFontSizeVariable.Large}>
                    {t("bookingRequest.proposal.timeOut.message")}
                </Text>
                <br></br>
                <Text fontSize={ThemeFontSizeVariable.Large}>
                    {t("bookingRequest.proposal.timeOut.messageOutro")}
                </Text>
            </GreetingWrapper>
        );
    }

    if (webBooking.proposalRejectedByStaff()) {
        return (
            <GreetingWrapper name={webBooking.contact.fname}>
                <Text fontSize={ThemeFontSizeVariable.Large}>
                    {t("bookingRequest.rejectedByStaff.message")}
                </Text>
                <br></br>
                <Text fontSize={ThemeFontSizeVariable.Large}>
                    {t("bookingRequest.rejectedByStaff.messageOutro")}
                </Text>
            </GreetingWrapper>
        );
    }

    if (hasCanceledStatus && isWaitListBooking) {
        return (
            <GreetingWrapper name={webBooking.contact.fname}>
                <Text fontSize={ThemeFontSizeVariable.Large}>
                    {t("waitList.waitListIsCanceled")}
                </Text>
            </GreetingWrapper>
        );
    }
    if (hasCanceledStatus) {
        return (
            <GreetingWrapper name={webBooking.contact.fname}>
                <Text fontSize={ThemeFontSizeVariable.Large}>
                    {t("bookingIsCanceled", { guests, date, startTime, endTime })}
                </Text>
            </GreetingWrapper>
        );
    }
    if (hasPassed) {
        return (
            <GreetingWrapper name={webBooking.contact.fname}>
                <Text fontSize={ThemeFontSizeVariable.Large}>
                    {t("theTimeHasPassed", { date })}
                </Text>
            </GreetingWrapper>
        );
    }
    if (hasWaitListOffers(webBooking)) {
        return <WaitListOffer />;
    }
    if (isCancelError) {
        return <CancelBookingFailed />;
    }
    return (
        <Greeting
            name={webBooking.contact.fname}
            isWaitList={isWaitListBooking}
            isInquiry={webBooking.isInquiry()}
        />
    );
};

export default BookingStatus;
